import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Alert,
  Stack,
} from '@mui/material';
import {
  Download,
  Visibility,
  VisibilityOff,
  Share,
  VerifiedUser,
} from '@mui/icons-material';
import { Certificate, trainingService } from '../services/trainingService';

interface CertificateDisplayProps {
  certificate: Certificate;
  onVisibilityChange?: () => void;
}

export default function CertificateDisplay({
  certificate,
  onVisibilityChange,
}: CertificateDisplayProps) {
  const [downloading, setDownloading] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const data = await trainingService.downloadCertificate(certificate.id);
      if (data.pdfUrl) {
        // Open PDF in new tab
        window.open(data.pdfUrl, '_blank');
      } else {
        setError('Certificate PDF is being generated. Please try again in a moment.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const handleToggleVisibility = async () => {
    setUpdatingVisibility(true);
    setError(null);
    try {
      await trainingService.updateCertificateVisibility(
        certificate.id,
        !certificate.isVisibleOnProfile
      );
      onVisibilityChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to update visibility');
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const handleShare = () => {
    // Copy certificate verification link
    const verifyUrl = `${window.location.origin}/verify-certificate/${certificate.certificateNumber}`;
    navigator.clipboard.writeText(verifyUrl);
    alert('Certificate verification link copied to clipboard!');
  };

  const isExpired = certificate.expiryDate
    ? new Date(certificate.expiryDate) < new Date()
    : false;

  return (
    <Card
      sx={{
        height: '100%',
        border: '2px solid',
        borderColor: certificate.isRevoked ? 'error.main' : isExpired ? 'warning.main' : 'success.main',
        position: 'relative',
      }}
    >
      {/* Certificate Header Banner */}
      <Box
        sx={{
          bgcolor: certificate.isRevoked ? 'error.main' : isExpired ? 'warning.main' : 'success.main',
          color: 'white',
          p: 2,
          textAlign: 'center',
        }}
      >
        <VerifiedUser sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" fontWeight="bold">
          Certificate of Completion
        </Typography>
      </Box>

      <CardContent>
        {/* Course Name */}
        <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center">
          {certificate.courseName}
        </Typography>

        {/* Certificate Number */}
        <Box textAlign="center" mb={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Certificate Number
          </Typography>
          <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
            {certificate.certificateNumber}
          </Typography>
        </Box>

        {/* Completion Date */}
        <Box textAlign="center" mb={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Completed On
          </Typography>
          <Typography variant="body2">
            {new Date(certificate.completionDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>

        {/* Expiry Date */}
        {certificate.expiryDate && (
          <Box textAlign="center" mb={2}>
            <Typography variant="caption" color="text.secondary" display="block">
              {isExpired ? 'Expired On' : 'Valid Until'}
            </Typography>
            <Typography variant="body2" color={isExpired ? 'error' : 'inherit'}>
              {new Date(certificate.expiryDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>
        )}

        {/* Status Badges */}
        <Box display="flex" justifyContent="center" gap={1} mb={2}>
          {certificate.isVisibleOnProfile && (
            <Chip label="Visible on Profile" size="small" color="primary" />
          )}
          {certificate.isRevoked && (
            <Chip label="Revoked" size="small" color="error" />
          )}
          {isExpired && (
            <Chip label="Expired" size="small" color="warning" />
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Actions */}
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={downloading || certificate.isRevoked}
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>

          <Box display="flex" gap={1}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={certificate.isVisibleOnProfile ? <VisibilityOff /> : <Visibility />}
              onClick={handleToggleVisibility}
              disabled={updatingVisibility || certificate.isRevoked}
            >
              {updatingVisibility
                ? 'Updating...'
                : certificate.isVisibleOnProfile
                ? 'Hide from Profile'
                : 'Show on Profile'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Share />}
              onClick={handleShare}
            >
              Share
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

